import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Search, Filter, ChevronDown, X } from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from 'date-fns';
import { de } from 'date-fns/locale';
import MobileBottomNav from '../layout/MobileBottomNav';
import MobileFiltersDrawer from '../layout/MobileFiltersDrawer';
import Tooltip from '../common/Tooltip';
import CategoryIcon from '../common/CategoryIcon';
import SectionLabel from '../common/SectionLabel';

const TransactionFilters = forwardRef(({
  onFilterChange,
  categories,
  onAddTransaction,
  onSettingsClick,
  onPlannedClick,
  plannedCount,
  balance,
  projectedBalance,
  showMobileCharts,
  onToggleMobileCharts,
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [uncategorizedOnly, setUncategorizedOnly] = useState(false);

  const [dateMode, setDateMode] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useImperativeHandle(ref, () => ({
    getCurrentDate: () => currentDate,
    changeMonth: (increment) => handleMonthChange(increment),
    openMobileFilters: () => setIsMobileFiltersOpen(true),
    hasActiveFilters: () => type !== 'all' || selectedCategories.length > 0 || uncategorizedOnly
  }));

  const applyFilters = useCallback(() => {
    let filterStart = '';
    let filterEnd = '';

    if (dateMode === 'month') {
      filterStart = format(startOfMonth(currentDate), 'yyyy-MM-dd');
      filterEnd = format(endOfMonth(currentDate), 'yyyy-MM-dd');
    } else if (dateMode === 'custom') {
      filterStart = startDate;
      filterEnd = endDate;
    }

    onFilterChange({
      search,
      type: type === 'all' ? undefined : type,
      categories: selectedCategories.length > 0 ? selectedCategories.map(c => c.id).join(',') : undefined,
      uncategorized: uncategorizedOnly,
      startDate: filterStart,
      endDate: filterEnd,
      dateMode
    });
  }, [search, type, selectedCategories, uncategorizedOnly, dateMode, currentDate, startDate, endDate, onFilterChange]);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      applyFilters();
      return;
    }
    const timer = setTimeout(() => { applyFilters(); }, 450);
    return () => clearTimeout(timer);
  }, [applyFilters]);

  const handleMonthChange = (increment) => {
    setCurrentDate(prev => {
      const next = increment > 0 ? addMonths(prev, 1) : subMonths(prev, 1);
      const maxFuture = addMonths(new Date(), 6);
      if (next > maxFuture && increment > 0) return prev;
      return next;
    });
  };

  const toggleCategory = (cat) => {
    setUncategorizedOnly(false);
    setSelectedCategories(prev =>
      prev.find(c => c.id === cat.id)
        ? prev.filter(c => c.id !== cat.id)
        : [...prev, cat]
    );
  };

  const handleResetFilters = () => {
    setType('all');
    setSelectedCategories([]);
    setUncategorizedOnly(false);
    setDateMode('month');
    setCurrentDate(new Date());
    setSearch('');
  };

  const hasActiveFilters = type !== 'all' || selectedCategories.length > 0 || uncategorizedOnly;

  return (
    <>
      {/* Desktop Filter Bar */}
      <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-neutral-100 mb-6">
        <div className="p-4 flex flex-row gap-3 justify-between items-center">
          {/* Date Mode Tabs */}
          <div className="flex items-center bg-neutral-100 rounded-xl p-1 gap-0.5">
            {[
              { value: 'month', label: 'Monat' },
              { value: 'all', label: 'Alle' },
              { value: 'custom', label: 'Bereich' }
            ].map(m => (
              <button
                key={m.value}
                onClick={() => setDateMode(m.value)}
                className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-all ${
                  dateMode === m.value
                    ? 'bg-white shadow-sm text-primary-700'
                    : 'text-neutral-500 hover:text-neutral-700'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {/* Month Navigator */}
          {dateMode === 'month' && (
            <div className="flex items-center bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
              <button
                onClick={() => handleMonthChange(-1)}
                className="px-3 py-2 text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700 transition-all"
              >
                <ChevronDown className="rotate-90" size={17} />
              </button>
              <div className="px-2 py-1 font-semibold text-sm text-neutral-800 min-w-[128px] text-center select-none">
                {format(currentDate, 'MMMM yyyy', { locale: de })}
              </div>
              <button
                onClick={() => handleMonthChange(1)}
                className="px-3 py-2 text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700 transition-all"
              >
                <ChevronDown className="-rotate-90" size={17} />
              </button>
            </div>
          )}

          {/* Custom Date Range */}
          {dateMode === 'custom' && (
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="p-2 border border-neutral-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="p-2 border border-neutral-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          )}

          {/* Search Input */}
          <div className="flex flex-1 relative max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              placeholder="Suchen..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block w-full pl-9 pr-3 py-2 border border-neutral-200 rounded-xl text-sm bg-neutral-50 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary-500 transition-all"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border transition-all text-sm font-semibold ${
              isOpen || hasActiveFilters
                ? 'bg-primary-50 border-primary-200 text-primary-700'
                : 'bg-white border-neutral-200 text-neutral-600 hover:bg-neutral-50 hover:border-neutral-300'
            }`}
          >
            <Filter size={16} />
            Filter
            {hasActiveFilters && (
              <span className="w-1.5 h-1.5 bg-primary-500 rounded-full" />
            )}
          </button>
        </div>

        {/* Expanded Filter Panel */}
        {isOpen && (
          <div className="border-t border-neutral-100 p-4 bg-neutral-50/70 rounded-b-2xl space-y-4">
            {/* Type */}
            <div>
              <SectionLabel className="block mb-2">Typ</SectionLabel>
              <div className="flex gap-2 flex-wrap">
                {[
                  { v: 'all', l: 'Alle' },
                  { v: 'expense', l: 'Ausgaben' },
                  { v: 'income', l: 'Einnahmen' }
                ].map(t => (
                  <button
                    key={t.v}
                    onClick={() => setType(t.v)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                      type === t.v
                        ? 'bg-primary-600 text-white shadow-sm'
                        : 'bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                    }`}
                  >
                    {t.l}
                  </button>
                ))}
                <button
                  onClick={() => {
                    setUncategorizedOnly(!uncategorizedOnly);
                    if (!uncategorizedOnly) setSelectedCategories([]);
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border transition-all ${
                    uncategorizedOnly
                      ? 'bg-amber-50 text-amber-700 border-amber-200 shadow-sm'
                      : 'bg-white border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                  }`}
                >
                  Unkategorisiert
                </button>
              </div>
            </div>

            {/* Categories */}
            {categories.length > 0 && (
              <div>
                <SectionLabel className="block mb-2">Kategorien</SectionLabel>
                <div className="flex flex-wrap gap-2">
                  {categories.map(cat => {
                    const isSelected = selectedCategories.find(c => c.id === cat.id);
                    return (
                      <button
                        key={cat.id}
                        onClick={() => toggleCategory(cat)}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm font-medium border transition-all ${
                          isSelected
                            ? 'bg-primary-100 text-primary-800 border-primary-200 shadow-sm'
                            : 'bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50'
                        }`}
                      >
                        <Tooltip content={cat.name}>
                          <CategoryIcon
                            name={cat.icon_name}
                            size={24}
                            className={isSelected ? 'text-primary-700' : 'text-neutral-500'}
                          />
                        </Tooltip>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Reset */}
            {(hasActiveFilters || dateMode === 'custom') && (
              <div className="flex justify-end pt-1">
                <button
                  onClick={handleResetFilters}
                  className="text-xs text-red-500 hover:text-red-700 font-semibold flex items-center gap-1 transition-colors"
                >
                  <X size={12} />
                  Filter zurücksetzen
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile Nav */}
      <MobileBottomNav
        currentDate={currentDate}
        onMonthChange={handleMonthChange}
        onAddTransaction={onAddTransaction}
        onSettingsClick={onSettingsClick}
        onFiltersClick={() => setIsMobileFiltersOpen(true)}
        onPlannedClick={onPlannedClick}
        onToggleCharts={onToggleMobileCharts}
        plannedCount={plannedCount}
        filtersActive={hasActiveFilters}
        balance={balance}
        plannedCostBalance={projectedBalance}
        showCharts={showMobileCharts}
        dateMode={dateMode}
      />

      {/* Mobile Filters Drawer */}
      <MobileFiltersDrawer
        isOpen={isMobileFiltersOpen}
        onClose={() => setIsMobileFiltersOpen(false)}
        search={search}
        onSearchChange={setSearch}
        type={type}
        onTypeChange={setType}
        categories={categories}
        selectedCategories={selectedCategories}
        onToggleCategory={toggleCategory}
        uncategorizedOnly={uncategorizedOnly}
        onToggleUncategorized={() => {
          setUncategorizedOnly(!uncategorizedOnly);
          if (!uncategorizedOnly) setSelectedCategories([]);
        }}
        dateMode={dateMode}
        onDateModeChange={setDateMode}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onResetFilters={handleResetFilters}
      />
    </>
  );
});

TransactionFilters.displayName = 'TransactionFilters';

export default TransactionFilters;
